
import { computed, defineComponent, PropType, reactive } from 'vue'

// @components
import NewTable from '@/ui-kit/NewTable/index.vue'
import NewTableActions from '@/ui-kit/NewTableActions/index.vue'
import NewTableAction from '@/ui-kit/NewTableAction/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

//
import RecordInvoiceRow from './RecordInvoiceRow.vue'
import RecordContainerRow from './RecordContainerRow.vue'
import RecordContainerInvoiceRow from './RecordContainerInvoiceRow.vue'

// @types
import {
    PlanningRecord,
    PlanningRecordContainer,
    PlanningRecordContainerInvoice,
    PlanningRecordInvoice,
} from '@/new-services/types/planning'

import { Provider } from '@/new-services/types/couriers'
import { formatDateFromBackend } from '@/common/helpers/formatDate'

const COLUMNS = [
    {
        name: 'Номер накладной / контейнер',
        key: 'invoiceNumber',
    },
    {
        name: 'Адрес',
        key: 'shortAddress',
    },
    {
        name: 'Кол-во стопов',
        key: 'stopsCount',
    },
    {
        name: 'Физ вес',
        key: 'weight',
    },
    {
        name: 'Объемный вес',
        key: 'volumeWeight',
    },
    {
        name: 'Кол-во мест',
        key: 'places',
    },
    {
        name: 'ЛО',
        key: 'waitList',
    },
    {
        name: 'На складе',
        key: 'statusId',
    },
    {
        name: 'Тип',
        key: 'shipmentType',
    },
    {
        name: 'Осталось',
        key: 'timer',
    },
    {
        name: 'Курьер',
        key: 'courier',
    },
    {
        name: 'Статус контейнера',
        key: 'containerStatus',
    },
    {
        name: 'Действия',
        key: 'actions',
    },
]

export default defineComponent({
    name: 'planning-record',
    components: {
        NewTable,
        NewTableActions,
        NewTableAction,
        NewCheckbox,
        NewButton,
        //
        RecordInvoiceRow,
        RecordContainerRow,
        RecordContainerInvoiceRow,
    },
    props: {
        record: {
            type: Object as PropType<PlanningRecord>,
            required: true,
        },
        isHold: {
            type: Boolean,
            default: false,
        },
        providers: {
            type: Array as PropType<Provider[]>,
            default: () => [],
        },
    },
    emits: [
        'move-invoice-to-container',
        'assign-containers-to-courier',
        'open-panel',
        'delete-invoice-from-container',
        'delete-container',
        'set-invoices-wave',
        'select-invoice',
        'unselect-invoice',
    ],
    setup(props, { emit }) {
        const state = reactive({
            is_invoices_opened: false,
            expanded_containers: [] as number[],
            selected_invoices: [] as number[],
            selected_containers: [] as number[],
        })

        const onSelectInvoice = (invoice: PlanningRecordInvoice) => {
            state.selected_invoices.push(invoice.id)
            emit('select-invoice', invoice.id)
        }

        const onUnselectInvoice = (invoice: PlanningRecordInvoice) => {
            const index = state.selected_invoices.findIndex(
                (invoice_id) => invoice_id === invoice.id
            )
            if (index >= 0) {
                state.selected_invoices.splice(index, 1)
                emit('unselect-invoice', invoice.id)
            }
        }

        const onSelectContainer = (container: PlanningRecordContainer) => {
            state.selected_containers.push(container.id)
            container.invoices.forEach((invoice) => {
                state.selected_invoices.push(invoice.id)
                emit('select-invoice', invoice.id)
            })
        }

        const onRowClicked = (row: PlanningRecordContainerInvoice) => {
            emit('open-panel', row)
        }

        const onUnselectContainer = (container: PlanningRecordContainer) => {
            const index = state.selected_containers.findIndex(
                (container_id) => container_id === container.id
            )
            if (index >= 0) {
                state.selected_containers.splice(index, 1)
            }

            container.invoices.forEach((invoice) => {
                const index_invoice = state.selected_invoices.findIndex(
                    (invoice_id) => invoice_id === invoice.id
                )
                if (index_invoice >= 0) {
                    state.selected_invoices.splice(index_invoice, 1)
                    emit('unselect-invoice', invoice.id)
                }
            })
        }

        const toggleInvoices = () => {
            state.is_invoices_opened = !state.is_invoices_opened
        }

        const isContainerExpanded = (container_id: number) => {
            return state.expanded_containers.includes(container_id)
        }

        const toggleContainer = (container_id: number) => {
            if (isContainerExpanded(container_id)) {
                const index = state.expanded_containers.findIndex(
                    (cntr_id) => cntr_id === container_id
                )
                if (index >= 0) {
                    state.expanded_containers.splice(index, 1)
                }
            } else {
                state.expanded_containers.push(container_id)
            }
        }

        const selectAllItems = () => {
            if (props.record.containers) {
                state.selected_containers = props.record.containers.map((container) => container.id)
            } else {
                state.selected_invoices = props.record.invoices.map((invoice) => invoice.id)
            }
        }

        const unselectAllItems = () => {
            // state.selected_invoices = []
            state.selected_containers = []
            state.selected_invoices = []
        }

        const selectedItemsCount = computed(() => {
            return state.selected_invoices.length + state.selected_containers.length
        })

        const isOneContainerSelected = computed(() => state.selected_containers.length === 1)

        const moveInvoiceToContainer = (invoice?: PlanningRecordInvoice) => {
            const invoice_ids = [...state.selected_invoices]

            if (invoice && invoice.id && !invoice_ids.includes(invoice.id)) {
                invoice_ids.push(invoice.id)
            }

            emit('move-invoice-to-container', invoice_ids)
        }

        const setInvoicesWave = () => {
            const invoice_ids = [...state.selected_invoices]
            emit('set-invoices-wave', invoice_ids)
        }

        const deleteInvoices = (containerId?: number, invoiceId?: number) => {
            if (containerId && invoiceId) {
                emit('delete-invoice-from-container', {
                    invoiceIds: [invoiceId],
                    containerId,
                })
            } else {
                let container_id: number | null = null

                for (const container of props.record.containers) {
                    for (const invoice of container.invoices) {
                        if (invoice.id === state.selected_invoices[0]) {
                            container_id = container.id
                            break
                        }
                    }

                    if (container_id) {
                        break
                    }
                }

                if (!container_id) return

                emit('delete-invoice-from-container', {
                    invoiceIds: state.selected_invoices,
                    containerId: container_id,
                })
            }
        }

        const assignContainersToCourier = () => {
            emit('assign-containers-to-courier', state.selected_containers)
        }

        const containerInvoicesIds = computed(() => {
            const arr: number[] = []

            if (props.record.containers) {
                props.record.containers.forEach((container) => {
                    container.invoices.forEach((invoice) => {
                        arr.push(invoice.id)
                    })
                })
            }

            return arr
        })

        const isDeleteInvoices = computed(() => {
            const arr = containerInvoicesIds.value,
                container_ids: number[] = []

            if (props.record.containers) {
                props.record.containers.forEach((container) => {
                    container.invoices.forEach((invoice) => {
                        if (state.selected_invoices.includes(invoice.id)) {
                            container_ids.push(container.id)
                        }
                    })
                })
            }

            let indicator = true
            state.selected_invoices.forEach((invoice_id) => {
                if (!arr.includes(invoice_id)) {
                    indicator = false
                    return
                }
            })

            return container_ids.every((id) => id === container_ids[0]) && indicator
        })

        const title = computed(() => {
            return props.isHold ? 'Хранение' : `Сектор ${props.record.name}`
        })
        const columns = computed(() => {
            return props.isHold
                ? COLUMNS.filter((c) => !['courier', 'actions'].includes(c.key))
                : COLUMNS
        })

        return {
            state,
            onSelectInvoice,
            onUnselectInvoice,
            onSelectContainer,
            onRowClicked,
            onUnselectContainer,
            toggleInvoices,
            isContainerExpanded,
            toggleContainer,
            selectAllItems,
            unselectAllItems,
            selectedItemsCount,
            moveInvoiceToContainer,
            deleteInvoices,
            assignContainersToCourier,
            //
            isOneContainerSelected,
            isDeleteInvoices,
            title,
            columns,
            setInvoicesWave,
            formatDateFromBackend
        }
    },
})
